import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { WorkflowHistoryItem, ActivityPriority } from '../../types/workflow/activity';
import { workflowService } from '../../services/workflowService';
import { formatDate } from './workflowHistoryUtils';
import { WorkflowHistoryTable } from './WorkflowDetailPage/sections/WorkflowHistoryTable';

interface WorkflowHistoryTimelineProps {
  batchId?: number;
  workflowId?: number;
}

type PriorityFilter = ActivityPriority | 'All';

/**
 * WorkflowHistoryTimeline Component
 *
 * Displays combined workflow history (transitions + activities) in a table layout.
 * Features priority filtering, date grouping, category icons, status chips, and metadata display.
 */
export const WorkflowHistoryTimeline: React.FC<WorkflowHistoryTimelineProps> = ({
  batchId,
  workflowId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [history, setHistory] = useState<WorkflowHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('All');

  const fetchHistory = useCallback(async () => {
    if (!workflowId && !batchId) return;
    setLoading(true);
    setError(null);
    try {
      const filterValue = priorityFilter === 'All'
        ? undefined
        : priorityFilter;
      const data = await workflowService.getCombinedHistory(
        workflowId ?? batchId!,
        filterValue
      );
      setHistory(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch history';
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [batchId, workflowId, priorityFilter, enqueueSnackbar]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetchHistory sets loading synchronously before the async fetch; refactoring the fetch-on-mount pattern is not behavior-preserving here
    fetchHistory();
  }, [fetchHistory]);

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: PriorityFilter | null) => {
    if (newFilter !== null) {
      setPriorityFilter(newFilter);
    }
  };

  // Filter history items based on selected priority filter
  const filteredHistory = history.filter((item) => {
    // Hide Activity items that duplicate Transition entries
    if (item.itemType === 'Activity' && (item.category === 'Status' || item.category === 'StatusTransition')) {
      return false;
    }
    // Hide Comment items (comments now live in dedicated Comments tab)
    if (item.itemType === 'Comment') {
      return false;
    }
    return true;
  });

  // Group history items by date
  const groupedHistory = filteredHistory.reduce<Record<string, WorkflowHistoryItem[]>>((groups, item) => {
    const dateKey = formatDate(item.changedAt);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(item);
    return groups;
  }, {});

  return (
    <Box>
      {/* Priority Filter */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Filter:
        </Typography>
        <ToggleButtonGroup
          value={priorityFilter}
          exclusive
          onChange={handleFilterChange}
          size="small"
        >
          <ToggleButton value="All">All</ToggleButton>
          <ToggleButton value="High">
            <Chip label="High" size="small" color="error" variant={priorityFilter === 'High' ? 'filled' : 'outlined'} sx={{ pointerEvents: 'none' }} />
          </ToggleButton>
          <ToggleButton value="Medium">
            <Chip label="Medium" size="small" color="warning" variant={priorityFilter === 'Medium' ? 'filled' : 'outlined'} sx={{ pointerEvents: 'none' }} />
          </ToggleButton>
          <ToggleButton value="Low">
            <Chip label="Low" size="small" color="info" variant={priorityFilter === 'Low' ? 'filled' : 'outlined'} sx={{ pointerEvents: 'none' }} />
          </ToggleButton>
        </ToggleButtonGroup>
        {!loading && (
          <Typography variant="caption" color="text.secondary">
            {filteredHistory.length} {filteredHistory.length === 1 ? 'event' : 'events'}
          </Typography>
        )}
      </Stack>

      <WorkflowHistoryTable
        groupedHistory={groupedHistory}
        loading={loading}
        error={error}
        priorityFilter={priorityFilter}
        totalCount={filteredHistory.length}
      />
    </Box>
  );
};

export default WorkflowHistoryTimeline;
